/**
 * Opportunity API Service
 * Provides hooks for interacting with the opportunities API
 */

import { useMemo } from 'react';

import { useAuthenticatedAxios } from './axiosInstance';

/**
 * Hook for opportunity API operations
 */
export function useOpportunityApi() {
    const api = useAuthenticatedAxios();

    return useMemo(
        () => ({
            /**
             * Fetch opportunities with filters and pagination
             */
            fetchOpportunities: async (params = {}) => {
                const response = await api.get('/api/opportunities', {
                    params,
                });
                return response.data;
            },

            /**
             * Fetch featured opportunities
             */
            fetchFeatured: async (limit = 6) => {
                const response = await api.get('/api/opportunities/featured', {
                    params: { limit },
                });
                return response.data;
            },

            /**
             * Search opportunities
             */
            search: async (query, params = {}) => {
                const response = await api.get('/api/opportunities/search', {
                    params: { q: query, ...params },
                });
                return response.data;
            },

            /**
             * Get opportunity types and filter options
             */
            fetchTypes: async () => {
                const response = await api.get('/api/opportunities/types');
                return response.data;
            },

            /**
             * Fetch single opportunity by ID
             */
            fetchOpportunity: async (id) => {
                const response = await api.get(`/api/opportunities/${id}`);
                return response.data;
            },

            /**
             * Fetch opportunities by organization
             */
            fetchByOrganization: async (orgId, params = {}) => {
                const response = await api.get(
                    `/api/opportunities/organization/${orgId}`,
                    { params }
                );
                return response.data;
            },

            // Calendar/Events endpoints

            /**
             * Fetch calendar events
             * @param {Object} params - { startDate, endDate, region, type, limit }
             */
            fetchCalendar: async (params = {}) => {
                const response = await api.get('/api/opportunities/calendar', {
                    params,
                });
                return response.data;
            },

            // Funding endpoints

            /**
             * Fetch funding opportunities
             * @param {Object} params - { fundingType, minAmount, maxAmount, seekingPartners, eligibleOrgType }
             */
            fetchFunding: async (params = {}) => {
                const response = await api.get('/api/opportunities/funding', {
                    params,
                });
                return response.data;
            },

            // Location-based endpoints

            /**
             * Fetch opportunities near a location
             * @param {number} lat - Latitude
             * @param {number} lng - Longitude
             * @param {Object} params - { radius, type, limit }
             */
            fetchNearby: async (lat, lng, params = {}) => {
                const response = await api.get('/api/opportunities/nearby', {
                    params: { lat, lng, ...params },
                });
                return response.data;
            },

            /**
             * Fetch opportunities for map display (GeoJSON)
             * @param {Object} params - { type, region, audience }
             */
            fetchMapData: async (params = {}) => {
                const response = await api.get('/api/opportunities/map', {
                    params,
                });
                return response.data;
            },

            // CRUD operations

            /**
             * Create new opportunity
             */
            createOpportunity: async (data) => {
                const response = await api.post('/api/opportunities', data);
                return response.data;
            },

            /**
             * Update opportunity
             */
            updateOpportunity: async (id, data) => {
                const response = await api.put(
                    `/api/opportunities/${id}`,
                    data
                );
                return response.data;
            },

            /**
             * Delete opportunity
             */
            deleteOpportunity: async (id) => {
                const response = await api.delete(`/api/opportunities/${id}`);
                return response.data;
            },

            /**
             * Publish opportunity
             */
            publishOpportunity: async (id) => {
                const response = await api.post(
                    `/api/opportunities/${id}/publish`
                );
                return response.data;
            },

            /**
             * Archive opportunity
             */
            archiveOpportunity: async (id) => {
                const response = await api.post(
                    `/api/opportunities/${id}/archive`
                );
                return response.data;
            },
        }),
        [api]
    );
}

export default useOpportunityApi;
